import React, { useState, useEffect, useMemo } from 'react';
import { 
  Typography, 
  Alert, 
  Box, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Chip, 
  Button,
  IconButton,
  Paper,
  Container,
  Pagination,
  Stack
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Clear as ClearIcon,
  ChevronLeft,
  ChevronRight 
} from '@mui/icons-material';
// Note: speaker cards are rendered as real <a> links via react-router's Link for crawlability.
import { useFilteredSpeakers, useHomepageSpeakers, useSpeakersMetadata } from '../hooks/useSpeakers';
import SpeakerCard from '../components/speakers/SpeakerCard';
import SEO from '../components/common/SEO';
import { Speaker } from '../services/wordpressApi';
import { utils } from '../services/wordpressApi';
import SpeakerCardSkeleton from '../components/speakers/SpeakerCardSkeleton';

const Browse: React.FC = () => {
  // Use the new server-side filtering hook
  const { 
    speakers, 
    loading: speakersLoading, 
    error: speakersError,
    totalPages,
    totalItems,
    currentPage,
    goToPage,
    updateFilters,
    hasNextPage,
    hasPrevPage,
    filters
  } = useFilteredSpeakers(1, 10);

  // Get metadata for filter dropdowns only
  const { speakersMetadata, loading: metadataLoading, error: metadataError } = useSpeakersMetadata();
  const { homepageSpeakers, loading: featuredLoading, error: featuredError } = useHomepageSpeakers();
  
  // Local state for form inputs
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [featuredSliderIndex, setFeaturedSliderIndex] = useState<number>(0);
  // Debounce search term to prevent too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const isLoading = speakersLoading || featuredLoading;
  const skeletonCount = 10;

  // Extract unique companies and topics from speakers metadata
  const { companies, topics } = useMemo(() => {
    const companiesSet = new Set<string>();
    const topicsSet = new Set<string>();

    speakersMetadata.forEach(speaker => {
      if (speaker.company) companiesSet.add(speaker.company);
      if (speaker.topic) {
        // Handle both array (new multi-select) and string (legacy comma-separated)
        const topicArray = Array.isArray(speaker.topic) ? speaker.topic : speaker.topic.split(',');
        topicArray.forEach((individualTopic: string) => {
          const trimmedTopic = individualTopic.trim();
          if (trimmedTopic) {
            topicsSet.add(trimmedTopic);
          }
        });
      }
    });

    return {
      companies: Array.from(companiesSet).sort(),
      topics: Array.from(topicsSet).sort()
    };
  }, [speakersMetadata]);

  const hasActiveFilters = debouncedSearchTerm || selectedCompany || selectedTopic;

  // Apply filters to server when they change
  useEffect(() => {
    const newFilters = {
      ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      ...(selectedCompany && { company: selectedCompany }),
      ...(selectedTopic && { topic: selectedTopic })
    };
    
    // console.log('🔍 [DEBUG] Applying filters to server:', newFilters);
    updateFilters(newFilters);
  }, [debouncedSearchTerm, selectedCompany, selectedTopic]); // Use debouncedSearchTerm

  const getSpeakerRoute = (speaker: Speaker): string => {
    const slug = (speaker as any).slug || utils.stripHTML(speaker.title?.rendered || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    return `/speaker/${slug || speaker.id}`;
  };

  const clearFilters = (): void => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedCompany('');
    setSelectedTopic('');
  };

  // Featured speakers slider logic (unchanged)
  const featuredSpeakers = useMemo(() => homepageSpeakers, [homepageSpeakers]);

  const nextFeaturedSlide = (): void => {
    setFeaturedSliderIndex(prev => 
      prev >= featuredSpeakers.length - 2 ? 0 : prev + 2
    );
  };

  const prevFeaturedSlide = (): void => {
    setFeaturedSliderIndex(prev => 
      prev <= 0 ? Math.max(0, featuredSpeakers.length - 2) : prev - 2
    );
  };

  const visibleFeaturedSpeakers = featuredSpeakers.slice(
    featuredSliderIndex, 
    featuredSliderIndex + 2
  );

  useEffect(() => {
    if (featuredSpeakers.length > 2) {
      const interval = setInterval(nextFeaturedSlide, 5000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [featuredSpeakers.length]);

  // Handle pagination click
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number): void => {
    goToPage(page);
    document.getElementById('speaker-grid')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Removed early return spinners. Render page immediately with skeleton tiles while loading.

  return (
    <>
      <SEO 
        title="Browse Keynote Speakers - CEO Keynotes"
        description="Discover and book from our curated collection of world-class CEOs, business leaders, and motivational speakers for your next corporate event, conference, or meeting."
        keywords="browse speakers, keynote speakers directory, CEO speakers, business leaders, corporate event speakers, motivational speakers, conference speakers, executive speakers"
        url="/browse"
      />
      
      <Box sx={{ 
        bgcolor: 'black', 
        minHeight: '100vh',
        color: 'white'
      }}>
        <Container maxWidth="xl" sx={{ py: 4, pt: 16 }}>
          {/* Header */}
          <Box sx={{ mb: { xs: 5, md: 8 }, textAlign: 'center' }}>
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom
              sx={{
                fontFamily: '"Manrope", sans-serif',
                fontWeight: 700,
                mb: 3,
                color: 'white',
                fontSize: {
                  xs: '1.8rem',  
                  sm: '2.0rem',    
                  md: '2.4rem'  
                }
              }}
            >
              Browse Our Speakers
            </Typography>
            <Typography 
              variant="h5" 
              sx={{
                fontFamily: '"Newsreader", serif',
                maxWidth: '800px',
                mx: 'auto',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: {
                  xs: '1.0rem',  
                  sm: '1.1rem',    
                  md: '1.25rem'  
                }
              }}
            >
              Find a world-class business leader for your next event
            </Typography>
          </Box>

          {/* Inline error, but keep page visible */}
          {(speakersError || featuredError || metadataError) && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Alert severity="error">
                Error loading speakers: {speakersError || featuredError || metadataError}
              </Alert>
            </Box>
          )}

          {/* Search and Filters */}
          <Paper elevation={1} sx={{ 
            p: 4, 
            mb: 6, 
            borderRadius: '12px',
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>


            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              gap: 3,
              mb:2
            }}>
              {/* Search Field */}
              <TextField
                fullWidth
                placeholder="Search by name, company, topic, or keyword..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.7)' }} />,
                }}
                sx={{ 
                  flex: 2,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'white',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1,
                  },
                }}
              />

              {/* Company Filter */}
              <FormControl sx={{ 
                flex: 1, 
                minWidth: 200,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiSelect-icon': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}>
                <InputLabel>Company</InputLabel>
                <Select
                  value={selectedCompany}
                  label="Company"
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">All Companies</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company} value={company}>
                      {company}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Topic Filter */}
              <FormControl sx={{ 
                flex: 1, 
                minWidth: 200,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiSelect-icon': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}>
                <InputLabel>Topic</InputLabel>
                <Select
                  value={selectedTopic}
                  label="Topic"
                  onChange={(e) => setSelectedTopic(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">ALL Topics</MenuItem>
                  {topics.map((topic) => (
                    <MenuItem key={topic} value={topic}>
                      {topic}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Active Filters and Clear Button */}
            {hasActiveFilters && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Active filters:
                </Typography>
               
                {searchTerm && (
                  <Chip
                    label={`Search: "${searchTerm}"`}
                    onDelete={() => {
                      setSearchTerm('');
                      setDebouncedSearchTerm('');
                    }}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                          color: 'white',
                        },
                      },
                    }}
                  />
                )}
                
                {selectedCompany && (
                  <Chip
                    label={`Company: ${selectedCompany}`}
                    onDelete={() => setSelectedCompany('')}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                          color: 'white',
                        },
                      },
                    }}
                  />
                )}
                
                {selectedTopic && (
                  <Chip
                    label={`Topic: ${selectedTopic}`}
                    onDelete={() => setSelectedTopic('')}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                          color: 'white',
                        },
                      },
                    }}
                  />
                )}

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  sx={{ 
                    ml: 'auto',
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Clear All
                </Button>
             </Box>
            )}
          </Paper>

          {/* Results Summary */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              sx={{
                fontFamily: '"Manrope", sans-serif',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.7)'
              }}
            >
                              {metadataLoading ? 'Loading results…' : (hasActiveFilters ? 
                  `Showing ${totalItems} matching speakers (page ${currentPage} of ${totalPages})` :
                  `All ${totalItems} speakers (page ${currentPage} of ${totalPages})`
                )}
            </Typography>
          </Box>

          {/* Speaker Grid */}
          <Box id="speaker-grid">
            {isLoading ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: 3,
                  maxWidth: '1400px',
                  mx: 'auto',
                }}
              >
                {Array.from({ length: skeletonCount }).map((_, idx) => (
                  <SpeakerCardSkeleton key={`skeleton-${idx}`} />
                ))}
              </Box>
            ) : (
              <>
                {speakers.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                      No speakers found
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
                      Try adjusting your search criteria or{' '}
                      <Button 
                        variant="text" 
                        onClick={clearFilters}
                        sx={{ 
                          textTransform: 'none', 
                          p: 0, 
                          verticalAlign: 'baseline',
                          color: 'white',
                          textDecoration: 'underline',
                          '&:hover': {
                            bgcolor: 'transparent',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        clear all filters
                      </Button>
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                      gap: 3,
                      maxWidth: '1400px',
                      mx: 'auto',
                    }}
                  >
                    {speakers.map((speaker: Speaker) => (
                      <SpeakerCard
                        key={speaker.id}
                        speaker={speaker}
                        to={getSpeakerRoute(speaker)}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Pagination Controls */}
          {totalPages > 1 && !isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, mb: 4 }}>
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        bgcolor: 'white',
                        color: 'black',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                        },
                      },
                    },
                  }}
                />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    textAlign: 'center', 
                    color: 'rgba(255, 255, 255, 0.7)' 
                  }}
                >
                  Page {currentPage} of {totalPages} • {totalItems} total speakers
                </Typography>
              </Stack>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default Browse; 